import React from 'react';
import type { CompanyHubHeaderData } from '../../../utils/seo/build-company-hub-header';
import { HubCrossLinks } from '../../../components/hub/HubCrossLinks';
import type { HubCrossLink } from '../../../types/hub-cross-link';
import { buildHubIndexLinks, hubLinksHeading } from '../../../utils/seo/hub-index-links';

type Locale = 'en' | 'fi' | 'sv';

// In-code locale maps, same reason as RoleHeader.tsx: part of the server-rendered SRP shell.
const STRINGS: Record<Locale, {
  h1: (brand: string) => string;
  openNow: (n: string) => string;
  intro: (brandInCopy: string) => string;
  profileLink: (brand: string) => string;
}> = {
  fi: {
    h1: (brand) => `${brand}: avoimet työpaikat`,
    openNow: (n) => `${n} avointa työpaikkaa juuri nyt`,
    intro: (brand) =>
      `Kaikki ${brand} tällä hetkellä auki olevat paikat suoraan työnantajan omilta sivuilta ja Työmarkkinatorilta. Työnantajaprofiilista löydät palkkatiedot, rekrytointitrendin ja etätyön osuuden.`,
    profileLink: (brand) => `${brand}: työnantajaprofiili ja palkat`,
  },
  sv: {
    h1: (brand) => `${brand}: lediga jobb`,
    openNow: (n) => `${n} lediga tjänster just nu`,
    intro: (brand) =>
      `Alla tjänster som ${brand} har öppna just nu, direkt från arbetsgivarens egna sidor och Työmarkkinatori. På arbetsgivarprofilen hittar du löner, rekryteringstrend och andelen distansjobb.`,
    profileLink: (brand) => `${brand}: arbetsgivarprofil och löner`,
  },
  en: {
    h1: (brand) => `${brand}: open jobs`,
    openNow: (n) => `${n} open positions right now`,
    intro: (brand) =>
      `Every position ${brand} has open right now, straight from the employer's own site and Työmarkkinatori. The employer profile has pay data, the hiring trend and the remote share.`,
    profileLink: (brand) => `${brand}: employer profile and pay`,
  },
};

function formatCount(n: number, locale: Locale): string {
  return locale === 'en' ? n.toLocaleString('en-US') : n.toLocaleString('fi-FI').replace(/ /g, ' ');
}

/** Finnish genitive of a brand for running copy: vowel-final "Attendon", consonant-final "Nokian"-style "in". */
export function brandGenitiveFi(brand: string): string {
  const b = brand.trim();
  if (!b) return b;
  const last = b[b.length - 1].toLowerCase();
  return 'aeiouyäö'.includes(last) ? `${b}n` : `${b}in`;
}

export interface CompanyHeaderProps {
  companyHub: CompanyHubHeaderData;
  locale: Locale;
}

/**
 * Static SEO header for one employer's job list on the SRP (`/jobs/employer/{id}`): the H1 the
 * page otherwise lacks, the live count, and links to the employer profile and the employer/role
 * hubs. Rendered by SearchHeader.tsx, all in the initial server HTML.
 */
export const CompanyHeader: React.FC<CompanyHeaderProps> = ({ companyHub, locale }) => {
  const t = STRINGS[locale];
  const { brand, jobCount, profileHref } = companyHub;
  const brandInCopy = locale === 'fi' ? brandGenitiveFi(brand) : brand;
  const links: HubCrossLink[] = [
    ...(profileHref ? [{ kind: 'employer' as const, href: profileHref, label: t.profileLink(brand) }] : []),
    ...buildHubIndexLinks(locale, ['employers', 'roles']),
  ];
  return (
    <section
      id="company-info-header"
      className="company-info-header relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[#F8F5F0] via-[#F2EADF] to-[#F5EDD0]/60 dark:from-[#170F07] dark:via-[#2A1D0F] dark:to-[#3A2A0A] p-6 sm:p-8 lg:p-10"
      aria-labelledby="company-header-title"
    >
      <h1 id="company-header-title" className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF] mb-2">
        {t.h1(brand)}
      </h1>
      {jobCount > 0 ? (
        <p className="text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] mb-3">{t.openNow(formatCount(jobCount, locale))}</p>
      ) : null}
      <p className="text-base leading-relaxed max-w-4xl text-[#3D3529] dark:text-[#C4A882] mb-5">{t.intro(brandInCopy)}</p>
      <HubCrossLinks variant="chips" links={links} locale={locale} heading={hubLinksHeading(locale)} />
    </section>
  );
};
